import { useState, useEffect, useCallback } from "react";
import { getActiveBanner, claimCoupon } from "../../api/couponBanner";
import { API_BASE_URL } from "../../api/config/axios";
import toast from "react-hot-toast";

/**
 * LeadCaptureModal - A compact popup modal for capturing leads with email
 * in exchange for a discount coupon code.
 */
export default function LeadCaptureModal() {
    const [banner, setBanner] = useState(null);
    const [isOpen, setIsOpen] = useState(false);
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [couponCode, setCouponCode] = useState(null);
    const [copied, setCopied] = useState(false);

    const hasSeenBanner = useCallback((bannerId) => {
        const seen = localStorage.getItem(`coupon_banner_seen_${bannerId}`);
        return !!seen;
    }, []);

    const markBannerAsSeen = useCallback((bannerId) => {
        localStorage.setItem(
            `coupon_banner_seen_${bannerId}`,
            Date.now().toString()
        );
    }, []);

    useEffect(() => {
        const fetchBanner = async () => {
            try {
                const response = await getActiveBanner();
                if (response.success && response.data) {
                    const bannerData = response.data;
                    if (!hasSeenBanner(bannerData.id)) {
                        setBanner(bannerData);
                        setupTrigger(bannerData);
                    }
                }
            } catch (err) {
                console.error("Error fetching banner:", err);
            }
        };
        fetchBanner();
    }, [hasSeenBanner]);

    const setupTrigger = useCallback((bannerData) => {
        const { triggerType, triggerDelay, scrollPercent } = bannerData;

        switch (triggerType) {
            case "EXIT_INTENT":
                const handleMouseLeave = (e) => {
                    if (e.clientY <= 0) {
                        setIsOpen(true);
                        document.removeEventListener(
                            "mouseleave",
                            handleMouseLeave
                        );
                    }
                };
                document.addEventListener("mouseleave", handleMouseLeave);
                return () =>
                    document.removeEventListener(
                        "mouseleave",
                        handleMouseLeave
                    );

            case "TIME_DELAY":
                const timer = setTimeout(
                    () => setIsOpen(true),
                    (triggerDelay || 5) * 1000
                );
                return () => clearTimeout(timer);

            case "SCROLL":
                const handleScroll = () => {
                    const scrolled =
                        (window.scrollY /
                            (document.documentElement.scrollHeight -
                                window.innerHeight)) *
                        100;
                    if (scrolled >= (scrollPercent || 50)) {
                        setIsOpen(true);
                        window.removeEventListener("scroll", handleScroll);
                    }
                };
                window.addEventListener("scroll", handleScroll);
                return () => window.removeEventListener("scroll", handleScroll);

            case "MANUAL":
                break;

            default:
                break;
        }
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!email || !emailRegex.test(email)) {
            setError("Please enter a valid email");
            return;
        }

        setLoading(true);

        try {
            const response = await claimCoupon(banner.id, email);
            if (response.success) {
                setSuccess(true);
                setCouponCode(response.data.code);
                markBannerAsSeen(banner.id);
                toast.success("Coupon claimed!");
            } else {
                setError(response.message || "Failed to claim coupon");
            }
        } catch (err) {
            const errorMessage =
                err.response?.data?.message || "Failed to claim coupon";
            if (err.response?.data?.code) {
                setSuccess(true);
                setCouponCode(err.response.data.code);
                markBannerAsSeen(banner.id);
            } else {
                setError(errorMessage);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setIsOpen(false);
        if (banner) markBannerAsSeen(banner.id);
    };

    const handleCopy = async () => {
        if (couponCode) {
            await navigator.clipboard.writeText(couponCode);
            setCopied(true);
            toast.success("Code copied!");
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const getDiscountText = () => {
        if (!banner) return "";
        return banner.discountType === "PERCENTAGE"
            ? `${banner.discountValue}%`
            : `£${banner.discountValue}`;
    };

    const handleManualTrigger = () => setIsOpen(true);

    const handleDismissManual = (e) => {
        e.stopPropagation();
        if (banner) {
            markBannerAsSeen(banner.id);
            setBanner(null);
        }
    };

    if (!banner) return null;

    // Manual trigger button (compact pill style with dismiss)
    const renderManualTriggerButton = () => {
        if (banner.triggerType !== "MANUAL" || isOpen) return null;

        return (
            <div className="fixed bottom-3 left-1/2 -translate-x-1/2 z-[9998] group/manual">
                {/* Dismiss Button */}
                <button
                    onClick={handleDismissManual}
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-white text-gray-400 hover:text-red-500 rounded-full shadow-md flex items-center justify-center transition-all opacity-0 group-hover/manual:opacity-100 z-[9999]"
                    title="Dismiss"
                >
                    <i className="ri-close-line text-xs font-bold"></i>
                </button>

                <button
                    onClick={handleManualTrigger}
                    className="group px-4 py-2 bg-gradient-to-r from-[#F3B11A] to-[#e5a617] text-[#01164F] text-sm font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-1.5"
                >
                    <i className="ri-gift-line text-base group-hover:animate-pulse"></i>
                    <span>{banner.buttonText || "Get Discount"}</span>
                    <span className="ml-1 px-1.5 py-0.5 bg-[#01164F] text-white text-xs rounded-full font-bold">
                        {getDiscountText()}
                    </span>
                </button>
            </div>
        );
    };

    if (!isOpen && banner.triggerType !== "MANUAL") return null;

    return (
        <>
            {renderManualTriggerButton()}

            {isOpen && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center p-3">
                    {/* Backdrop */}
                    <div
                        className="absolute inset-0 bg-black/50 backdrop-blur-[2px]"
                        onClick={handleClose}
                    />

                    {/* Modal - Compact */}
                    <div className="relative w-full max-w-sm bg-white rounded-xl shadow-2xl overflow-hidden animate-modalIn">
                        {/* Close button */}
                        <button
                            onClick={handleClose}
                            className="absolute top-2 right-2 z-20 w-6 h-6 flex items-center justify-center rounded-full bg-white/80 hover:bg-white text-gray-600 hover:text-gray-900 transition-all shadow-sm"
                        >
                            <i className="ri-close-line text-sm"></i>
                        </button>

                        {/* Header with gradient and discount badge */}
                        <div className="relative bg-gradient-to-br from-[#01164F] via-[#0a2463] to-[#142c5c] px-4 py-5 text-center">
                            {/* Background pattern */}
                            <div className="absolute inset-0 opacity-10">
                                <div className="absolute top-0 left-0 w-20 h-20 bg-white rounded-full -translate-x-10 -translate-y-10"></div>
                                <div className="absolute bottom-0 right-0 w-32 h-32 bg-white rounded-full translate-x-16 translate-y-16"></div>
                            </div>

                            {/* Discount Badge */}
                            <div className="relative inline-flex items-center justify-center mb-2">
                                <div className="absolute w-16 h-16 bg-[#F3B11A]/20 rounded-full animate-ping-slow"></div>
                                <div className="relative w-14 h-14 bg-gradient-to-br from-[#F3B11A] to-[#e5a617] rounded-full flex items-center justify-center shadow-lg">
                                    <span className="text-[#01164F] font-bold text-lg">
                                        {getDiscountText()}
                                    </span>
                                </div>
                            </div>

                            <h2 className="relative text-white text-lg font-bold leading-tight">
                                {banner.title}
                            </h2>
                            {banner.description && (
                                <p className="relative text-blue-200 text-xs mt-1 opacity-90">
                                    {banner.description}
                                </p>
                            )}
                        </div>

                        {/* Banner Image (optional, smaller) */}
                        {banner.imageUrl && (
                            <div className="h-20 overflow-hidden">
                                <img
                                    src={API_BASE_URL + banner.imageUrl}
                                    alt={banner.title}
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        )}

                        {/* Content */}
                        <div className="p-4">
                            {!success ? (
                                <>
                                    <form
                                        onSubmit={handleSubmit}
                                        className="space-y-3"
                                    >
                                        <div>
                                            <div className="relative">
                                                <i className="ri-mail-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                                                <input
                                                    type="email"
                                                    value={email}
                                                    onChange={(e) => {
                                                        setEmail(
                                                            e.target.value
                                                        );
                                                        setError(null);
                                                    }}
                                                    placeholder="Enter your email"
                                                    className="w-full pl-9 pr-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F3B11A]/50 focus:border-[#F3B11A] transition-all"
                                                />
                                            </div>
                                            {error && (
                                                <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                                                    <i className="ri-error-warning-line"></i>
                                                    {error}
                                                </p>
                                            )}
                                        </div>

                                        <button
                                            type="submit"
                                            disabled={loading}
                                            className="w-full py-2.5 bg-gradient-to-r from-[#F3B11A] to-[#e5a617] text-[#01164F] text-sm font-bold rounded-lg hover:shadow-md transition-all disabled:opacity-50 flex items-center justify-center gap-1.5"
                                        >
                                            {loading ? (
                                                <>
                                                    <i className="ri-loader-4-line animate-spin"></i>
                                                    <span>Claiming...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <i className="ri-gift-line"></i>
                                                    <span>
                                                        {banner.buttonText ||
                                                            "Get My Discount"}
                                                    </span>
                                                </>
                                            )}
                                        </button>
                                    </form>

                                    {banner.minOrderAmount && (
                                        <p className="text-[10px] text-gray-400 text-center mt-2">
                                            *Min. order £{banner.minOrderAmount}
                                        </p>
                                    )}
                                </>
                            ) : (
                                /* Success State - Compact & Email Only */
                                <div className="text-center py-4">
                                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                                        <i className="ri-mail-send-line text-2xl text-green-600 animate-bounce"></i>
                                    </div>
                                    <h3 className="text-[#01164F] font-bold text-lg mb-1">
                                        Success!
                                    </h3>
                                    <p className="text-gray-600 text-sm mb-5 leading-relaxed">
                                        Your exclusive{" "}
                                        <span className="font-bold text-[#F3B11A]">
                                            {getDiscountText()} discount
                                        </span>{" "}
                                        has been sent to:
                                        <br />
                                        <span className="font-semibold text-[#01164F]">
                                            {email}
                                        </span>
                                    </p>

                                    <button
                                        onClick={handleClose}
                                        className="w-full py-2.5 bg-[#01164F] text-white text-sm font-semibold rounded-lg hover:bg-[#0a2463] transition-all shadow-md active:scale-[0.98]"
                                    >
                                        Continue Shopping
                                    </button>

                                    <p className="text-[10px] text-gray-400 mt-4">
                                        Didn't receive it? Check your spam
                                        folder.
                                    </p>
                                </div>
                            )}

                            {/* Terms */}
                            {banner.termsAndConditions && (
                                <p className="text-[9px] text-gray-400 text-center mt-3 leading-relaxed">
                                    {banner.termsAndConditions}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Animations */}
                    <style>{`
                        @keyframes modalIn {
                            from {
                                opacity: 0;
                                transform: scale(0.9) translateY(20px);
                            }
                            to {
                                opacity: 1;
                                transform: scale(1) translateY(0);
                            }
                        }
                        .animate-modalIn {
                            animation: modalIn 0.25s ease-out forwards;
                        }
                        @keyframes ping-slow {
                            0% { transform: scale(1); opacity: 0.5; }
                            75%, 100% { transform: scale(1.5); opacity: 0; }
                        }
                        .animate-ping-slow {
                            animation: ping-slow 2s cubic-bezier(0, 0, 0.2, 1) infinite;
                        }
                    `}</style>
                </div>
            )}
        </>
    );
}
